/**
 * catastroAvailability.ts
 *
 * Detección de disponibilidad de la Sede Electrónica del Catastro (SEC).
 *
 * Arquitectura real verificada (2026-03-14):
 *   www1.sedecatastro.gob.es  — SEC: fichas HTML, paradas el 2º miércoles ≥ 15 h
 *   ovc.catastro.meh.es       — OVC: APIs XML + WMS. Independiente, sin paradas
 *                                documentadas. NO tiene fichas HTML navegables.
 *                                No sirve como respaldo visual para el técnico.
 *
 * Estados posibles:
 *   'ok'          — Servidor disponible, fuera de ventana de mantenimiento
 *   'maintenance' — Dentro de la ventana de mantenimiento programado (potencial)
 *   'down'        — Servidor no responde (timeout o error de red)
 *   'unknown'     — Estado inicial mientras se realiza el chequeo
 *
 * @module utils/catastroAvailability
 * @version 1.0.0
 * @see https://www.catastro.hacienda.gob.es/ayuda/paradas_sede.htm
 */

import { useState, useEffect } from 'react';

// ============================================================================
// CONSTANTES PÚBLICAS
// ============================================================================

/** Página oficial de paradas programadas de la SEC */
export const CATASTRO_PARADAS_URL =
  'https://www.catastro.hacienda.gob.es/ayuda/paradas_sede.htm';

/** Timeout del chequeo de disponibilidad (ms) */
export const CATASTRO_CHECK_TIMEOUT_MS = 4000;

/** TTL de la caché del chequeo — evita peticiones repetidas (ms) */
export const CATASTRO_CHECK_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutos

// ============================================================================
// TIPOS
// ============================================================================

export type CatastroStatus =
  | 'ok'           // Servidor disponible
  | 'maintenance'  // Ventana de mantenimiento programado (2º miércoles ≥ 15 h)
  | 'down'         // No responde
  | 'unknown';     // Chequeo en curso

// ============================================================================
// VENTANA DE MANTENIMIENTO
// ============================================================================

/**
 * Determina si una fecha/hora cae dentro de la ventana de mantenimiento
 * programado de la SEC: **segundo miércoles de cada mes a partir de las 15 h**.
 *
 * La DGC indica que las paradas «podrán no producirse» y «podrán variar de
 * fecha si coinciden con días festivos». Esta función detecta la ventana
 * potencial — el técnico debe consultar la página de paradas para confirmación.
 *
 * @param date - Fecha a comprobar (por defecto: ahora)
 */
export function isCatastroMaintenanceWindow(date: Date = new Date()): boolean {
  // Solo miércoles (0=dom, 3=mié, 6=sáb)
  if (date.getDay() !== 3) return false;

  // Solo a partir de las 15:00 h (hora local del navegador)
  if (date.getHours() < 15) return false;

  // El segundo miércoles del mes cae siempre entre el día 8 y el 14 (inclusive)
  const day = date.getDate();
  return day >= 8 && day <= 14;
}

// ============================================================================
// CHEQUEO DE SERVIDOR (con caché en memoria)
// ============================================================================

interface CacheEntry {
  status: 'ok' | 'down';
  timestamp: number;
}

let _cache: CacheEntry | null = null;

/**
 * Comprueba si www1.sedecatastro.gob.es responde.
 *
 * Usa `mode: 'no-cors'` para evitar bloqueo CORS desde el navegador.
 * Con ese modo, una respuesta opaca (type='opaque') indica que el servidor
 * responde; un NetworkError o AbortError indica que está caído.
 *
 * El resultado se cachea durante CATASTRO_CHECK_CACHE_TTL_MS para no
 * saturar con peticiones durante la misma sesión del técnico.
 *
 * @returns 'ok' si el servidor responde, 'down' si falla o supera el timeout
 */
export async function checkCatastroServer(): Promise<'ok' | 'down'> {
  if (_cache && Date.now() - _cache.timestamp < CATASTRO_CHECK_CACHE_TTL_MS) {
    return _cache.status;
  }

  try {
    await fetch('https://www1.sedecatastro.gob.es/', {
      method: 'HEAD',
      mode: 'no-cors',
      signal: AbortSignal.timeout(CATASTRO_CHECK_TIMEOUT_MS),
    });
    _cache = { status: 'ok', timestamp: Date.now() };
    return 'ok';
  } catch {
    _cache = { status: 'down', timestamp: Date.now() };
    return 'down';
  }
}

/** Resetea la caché del chequeo. Útil en tests y en cambios de sesión. */
export function resetCatastroServerCache(): void {
  _cache = null;
}

// ============================================================================
// HOOK REACT
// ============================================================================

/**
 * Hook que combina ventana de mantenimiento + chequeo de servidor en un
 * único estado reactivo para consumir en componentes UI.
 *
 * - Si hay ventana de mantenimiento activa, devuelve 'maintenance' sin hacer fetch.
 * - Si no hay ventana, verifica el servidor y devuelve 'ok' o 'down'.
 * - El estado inicial es 'unknown' durante el chequeo asíncrono.
 *
 * @example
 * ```tsx
 * const { status } = useCatastroStatus();
 * // 'unknown' → 'ok' | 'maintenance' | 'down'
 * ```
 */
export function useCatastroStatus(): { status: CatastroStatus } {
  const [status, setStatus] = useState<CatastroStatus>(() => {
    // Inicialización síncrona: si hay ventana de mantenimiento ya lo sabemos
    return isCatastroMaintenanceWindow() ? 'maintenance' : 'unknown';
  });

  useEffect(() => {
    // Si ya detectamos mantenimiento en la inicialización, no necesitamos fetch
    if (status === 'maintenance') return;

    let cancelled = false;
    checkCatastroServer().then((serverStatus) => {
      if (!cancelled) {
        setStatus(serverStatus === 'ok' ? 'ok' : 'down');
      }
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo al montar — el estado no cambia durante la sesión del técnico

  return { status };
}
